import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import AdminLayout from "../../components/AdminLayout";
import { grouplist, grpdelete, groupdetails } from "../../components/funtionsBD/Mainservice";
import Deletepopup from "./Aresure";
import { usePermissions } from "../../context/Permissioncontext";
import { useBrand } from "../../context/BrandContext";
import { GroupDetailsDialog } from "../../components/AdminLayout/GroupDetailsDialog";
import GroupMembersOverviewDialog from "../../components/GroupMembersOverviewDialog";
import { readActiveHomeRole } from "../../utils/homeRole";
import { resolveClubImageUrl } from "../../utils/resolveClubImageUrl";
import { useTourDemoActive } from "../../tour/useTourDemoActive";
import { demoGroupListRows } from "../../tour/tourDemoFixtures";
import "./Grouplist.css";

const EMPTY_GROUP_MEMBERS = {
  players: [],
  coaches: [],
  objectiveSummary: { primary: [], secondary: [] },
};

const LANE_TONES = ["pine", "ink", "clay", "slate", "lagoon"];

const MODE_KEYS = {
  coach: "groupsBoardModeCoach",
  manual: "groupsBoardModeManual",
  wizard: "groupsBoardModeWizard",
  auto: "groupsBoardModeAuto",
  seasonal: "groupsBoardModeSeasonal",
};

function laneTone(name) {
  const s = String(name || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return LANE_TONES[h % LANE_TONES.length];
}

function modeLabel(raw, t) {
  if (!raw) return "";
  const key = MODE_KEYS[String(raw).toLowerCase()];
  return key ? t(key) : String(raw);
}

const COURT_SEATS = 8;

function seatedCount(playerCount, maxPlayers, seats = COURT_SEATS) {
  const players = Number(playerCount) || 0;
  if (!players) return 0;
  return Math.max(
    1,
    Math.min(seats, Math.round((players / Math.max(Number(maxPlayers) || 1, 1)) * seats))
  );
}

function CourtGraphic({ seated, className = "" }) {
  return (
    <div className={`gl-court${className ? ` ${className}` : ""}`} aria-hidden="true">
      <span className="gl-court__surface" />
      <span className="gl-court__mid" />
      <span className="gl-court__net" />
      {Array.from({ length: COURT_SEATS }, (_, seat) => (
        <span
          key={seat}
          className={`gl-seat gl-seat--${seat}${seat < seated ? " is-on" : ""}`}
        />
      ))}
    </div>
  );
}

export function Grouplist({ embedded = false }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setdata] = useState();
  const [id, setid] = useState("");
  const [showdelete, setshowdelete] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupMembersDialogOpen, setGroupMembersDialogOpen] = useState(false);
  const [selectedGroupForMembers, setSelectedGroupForMembers] = useState(null);
  const [groupMembers, setGroupMembers] = useState(EMPTY_GROUP_MEMBERS);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const clubLogo = resolveClubImageUrl(brand?.logo_url);
  const clubName = brand?.brand_name || t("groupsBoardKicker");
  const location = useLocation();
  const info = JSON.parse(localStorage.getItem("user"));
  const tourDemo = useTourDemoActive();
  const rows = !data?.length && tourDemo ? demoGroupListRows() : data;
  const canView = hasPermission("VIEW_GROUP");
  const canCreate = hasPermission("CREATE_GROUP");
  const canUpdate = hasPermission("UPDATE_GROUP");
  const canDelete = hasPermission("DELETE_GROUP");

  useEffect(() => {
    const role = readActiveHomeRole(info);
    const typeId = Number(info?.type_user_id);
    if (role === "player" || (typeId === 4 && role !== "coach" && role !== "admin")) {
      navigate("/home", { replace: true });
      return;
    }
    getuser();

    const handleClubOrUserChange = () => {
      getuser();
    };
    window.addEventListener("smarttt-club-changed", handleClubOrUserChange);
    window.addEventListener("smarttt-user-updated", handleClubOrUserChange);
    return () => {
      window.removeEventListener("smarttt-club-changed", handleClubOrUserChange);
      window.removeEventListener("smarttt-user-updated", handleClubOrUserChange);
    };
  }, []);

  useEffect(() => {
    const openGroupId = location.state?.openGroupId;
    if (!openGroupId) return;
    getuser();
    setSelectedGroupId(openGroupId);
    setDetailsDialogOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state?.openGroupId]);

  const getuser = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
      const params = {
        club_id: currentUser?.club_id,
      };
      const response = await grouplist(params);
      setdata(response.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const handledelete = async () => {
    try {
      const params = {
        gr_id: id,
      };
      await grpdelete(params);
      getuser();
      setshowdelete(false);
    } catch (error) {
      if (error.code === 401) {
        localStorage.clear();
        navigate("/");
      }
      setshowdelete(false);
    }
  };

  const onClose = () => {
    setshowdelete(false);
  };

  const closeGroupMembersDialog = () => {
    setGroupMembersDialogOpen(false);
    setSelectedGroupForMembers(null);
    setGroupMembers(EMPTY_GROUP_MEMBERS);
  };

  const fetchGroupMembers = async (gr_id) => {
    if (!gr_id) return;
    setLoadingMembers(true);
    try {
      const response = await groupdetails(gr_id);
      const groupData = response.data.data || {};
      const summary = groupData.groupObjectiveSummary || {
        primary: [],
        secondary: [],
      };
      setGroupMembers({
        players: groupData.tbl_players || [],
        coaches: groupData.tbl_coaches || [],
        objectiveSummary: {
          primary: Array.isArray(summary.primary) ? summary.primary : [],
          secondary: Array.isArray(summary.secondary) ? summary.secondary : [],
        },
      });
    } catch (error) {
      console.error("Error fetching group members:", error);
      setGroupMembers(EMPTY_GROUP_MEMBERS);
    } finally {
      setLoadingMembers(false);
    }
  };

  const openGroupOverview = (item) => {
    if (!item?.group?.gr_id) return;
    setSelectedGroupForMembers(item);
    setGroupMembersDialogOpen(true);
    fetchGroupMembers(item.group.gr_id);
  };

  const openGroupContent = (item) => {
    if (!canView || !item?.group?.gr_id) return;
    setSelectedGroupId(item.group.gr_id);
    setDetailsDialogOpen(true);
  };

  const filteredRows = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) =>
      String(item?.group?.gr_name || "").toLowerCase().includes(q)
    );
  }, [rows, searchQuery]);

  const maxPlayers = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return Math.max(1, ...list.map((item) => Number(item?.playerCount) || 0));
  }, [rows]);

  const totals = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return {
      groups: list.length,
      players: list.reduce((sum, item) => sum + (Number(item?.playerCount) || 0), 0),
    };
  }, [rows]);

  const hasRows = Array.isArray(rows) && rows.length > 0;
  const showSearchEmpty = hasRows && filteredRows.length === 0;

  const addLink = canCreate ? (
    <NavLink to="/GroupsAdd" className="gl-hero__add">
      <Plus size={16} strokeWidth={2.4} aria-hidden="true" />
      {t("addgroup")}
    </NavLink>
  ) : null;

  const board = (
    <div
      className={`gl-board${embedded ? " gl-board--embedded" : ""}`}
      data-tour-id="admin-groups-page"
    >
      <div className="gl-board__glow" aria-hidden="true" />

      <header className="gl-hero">
        <div className="gl-hero__copy">
          <div className="gl-hero__club">
            {clubLogo ? <img src={clubLogo} alt="" /> : null}
            <span className="gl-hero__kicker">{clubName}</span>
          </div>
          <h1 className="gl-hero__title">{t("grouplist")}</h1>
          <p className="gl-hero__lede">{t("groupsBoardLede")}</p>
        </div>
        {addLink}
      </header>

      <div className="gl-toolbar">
        <label className="gl-search">
          <Search size={16} strokeWidth={2.2} aria-hidden="true" />
          <span className="sr-only">{t("groupsBoardSearch")}</span>
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t("groupsBoardSearch")}
            autoComplete="off"
          />
        </label>
        {hasRows ? (
          <div className="gl-pills" aria-live="polite">
            <span className="gl-pill">
              <strong>{totals.groups}</strong>
              {t("totalgrp")}
            </span>
            <span className="gl-pill">
              <strong>{totals.players}</strong>
              {t("totalplayer")}
            </span>
          </div>
        ) : null}
        {hasRows ? (
          <p className="gl-toolbar__hint">{t("groupsBoardOccupancyHint")}</p>
        ) : null}
      </div>

      {filteredRows.length > 0 ? (
        <div className="gl-lanes">
          {filteredRows.map((item, index) => {
            const name = item?.group?.gr_name || "";
            const tone = laneTone(name);
            const mode = modeLabel(item?.group?.gr_traning_session_type?.ts_gen_mode, t);
            const players = Number(item?.playerCount) || 0;
            const seated = seatedCount(players, maxPlayers);
            const groupId = item?.group?.gr_id || item?.id || index;
            const tableNo = String(index + 1).padStart(2, "0");

            return (
              <article key={groupId} className={`gl-lane gl-lane--${tone}`}>
                <div className="gl-lane__tape" aria-hidden="true">
                  <span className="gl-lane__no">{tableNo}</span>
                </div>
                <div
                  className={`gl-lane__main${canView ? " is-open" : ""}`}
                  role={canView ? "button" : undefined}
                  tabIndex={canView ? 0 : undefined}
                  onClick={() => openGroupContent(item)}
                  onKeyDown={(event) => {
                    if (!canView) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openGroupContent(item);
                    }
                  }}
                  aria-label={canView ? `${t("groupsBoardOpenContent")}: ${name}` : name}
                >
                  <div className="gl-lane__who">
                    <div className="gl-lane__name-row">
                      <h2 className="gl-lane__name">{name}</h2>
                      {mode ? <span className="gl-lane__mode">{mode}</span> : null}
                    </div>
                    <div className="gl-lane__meta">
                      <span>
                        <strong>{item?.coachCount ?? 0}</strong>
                        {t("totalcoach")}
                      </span>
                      <span>
                        <strong>{item?.totalSessions ?? 0}</strong>
                        {t("session")}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="gl-occupancy"
                  onClick={() => openGroupOverview(item)}
                  aria-label={`${t("groupsBoardOpenMembers")}: ${name}`}
                >
                  <CourtGraphic seated={seated} />
                  <span className="gl-occupancy__stat">
                    <span className="gl-occupancy__n">{players}</span>
                    <span className="gl-occupancy__label">{t("totalplayer")}</span>
                  </span>
                </button>
                {(canUpdate || canDelete) ? (
                <div className="gl-lane__actions">
                  {canUpdate ? (
                    <button
                      type="button"
                      className="gl-icon"
                      onClick={() => {
                        navigate("/EditGroup", {
                          state: { id: item.group.gr_id },
                        });
                      }}
                      aria-label={t("groupsBoardEdit")}
                      title={t("editgroup")}
                    >
                      <Pencil size={17} strokeWidth={2.1} />
                    </button>
                  ) : null}
                  {canDelete ? (
                    <button
                      type="button"
                      className="gl-icon gl-icon--danger"
                      onClick={() => {
                        setid(item.group.gr_id);
                        setshowdelete(true);
                      }}
                      aria-label={t("groupsBoardDelete")}
                      title={t("delete")}
                    >
                      <Trash2 size={17} strokeWidth={2.1} />
                    </button>
                  ) : null}
                </div>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="gl-empty">
          <CourtGraphic seated={0} className="gl-empty__court" />
          <p>{showSearchEmpty ? t("groupsBoardEmptySearch") : t("groupsBoardEmpty")}</p>
          {!showSearchEmpty ? addLink : null}
        </div>
      )}

      <Deletepopup
        isOpen={showdelete}
        setisOpen={setshowdelete}
        handledelete={handledelete}
        onClose={onClose}
      />
      <GroupDetailsDialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedGroupId(null);
        }}
        groupId={selectedGroupId}
      />
      <GroupMembersOverviewDialog
        open={groupMembersDialogOpen}
        onClose={closeGroupMembersDialog}
        group={selectedGroupForMembers}
        members={groupMembers}
        loading={loadingMembers}
      />
    </div>
  );

  if (embedded) return board;
  return <AdminLayout>{board}</AdminLayout>;
}
